import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent } from '@/lib/analytics';

/**
 * Tracks scroll depth at 25%, 50%, 75%, 90% thresholds
 * Fires once per threshold per page view, resets on route change
 */
export function useScrollDepthTracking() {
  const location = useLocation();

  useEffect(() => {
    const thresholds = [25, 50, 75, 90];
    const fired = new Set<number>();

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      if (scrollHeight <= 0) return;

      const percent = Math.round((scrollTop / scrollHeight) * 100);

      for (const t of thresholds) {
        if (percent >= t && !fired.has(t)) {
          fired.add(t);
          trackEvent('scroll_depth', {
            event_category: 'engagement',
            event_label: `Scroll ${t}%`,
            percent_scrolled: t,
            page_path: location.pathname,
          });
        }
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    // Check initial scroll position (for page refreshes mid-scroll)
    onScroll();

    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]); // Reset thresholds on route change
}
